import { DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild
} from '@angular/core';
import { IMenuSettings } from './interfaces/menu-settings.interface';

@Component({
  selector: 'sgt-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss']
})
export class MenuComponent implements OnInit, OnDestroy {
  @ViewChild('trigger', { static: false }) triggerElement!: ElementRef;
  @ViewChild('menuContainer', { static: false }) menuContainer?: ElementRef;

  private readonly defaultSettings: IMenuSettings = {
    position: 'bottom',
    closeOnSelect: true,
    width: 'auto',
    height: 'auto',
    isToCloseMenuOnScroll: false
  };

  isOpen = false;
  openUpward = false;

  @Input() settings!: IMenuSettings;

  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();
  @Output() itemSelected = new EventEmitter<unknown>();

  @HostBinding('class.sgt-menu--open') get isOpenClass(): boolean {
    return this.isOpen;
  }

  private scrollHandler = (event: Event): void => {
    if (this.settings?.isToCloseMenuOnScroll) {
      const el = this.menuContainer?.nativeElement;
      if (el && el.contains(event.target as Node)) {
        return; // scroll interno — ignora
      }
      this.closeMenu();
    } else {
      this.applyPosition();
    }
  };

  private onCapture = (event: MouseEvent) => {
    if (!this.isOpen) {
      return;
    }
    const insideTrigger = this.triggerElement?.nativeElement?.contains(
      event.target as Node
    );
    const insideMenu = this.menuContainer?.nativeElement?.contains(
      event.target as Node
    );

    if (!insideTrigger && !insideMenu) {
      this.closeMenu();
    }
  };

  constructor(
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document
  ) {}

  ngOnInit(): void {
    this.settings = { ...this.defaultSettings, ...this.settings };
  }

  /**
   * `openMenu()` move o container para o `<body>`; sem fechar aqui, ele
   * fica órfão quando o pai destrói o componente.
   */
  ngOnDestroy(): void {
    this.closeMenu();
    this.removeListeners();
  }

  private applyPosition(): void {
    if (
      !this.menuContainer?.nativeElement ||
      !this.triggerElement?.nativeElement
    ) {
      return;
    }

    const rect: DOMRect =
      this.triggerElement.nativeElement.getBoundingClientRect();
    const padding = 10;
    const gap = 4;
    const spaceBelow = window.innerHeight - rect.bottom - padding;
    const spaceAbove = rect.top - padding;
    this.openUpward = spaceAbove > spaceBelow;

    const maxHeight = Math.min(this.openUpward ? spaceAbove : spaceBelow, 300);

    const el: HTMLElement = this.menuContainer.nativeElement;
    if (this.settings?.width && this.settings.width !== 'auto') {
      el.style.width = this.settings.width;
    }

    const largura = el.offsetWidth;
    const cabeADireita = rect.left + largura <= window.innerWidth - padding;
    const left = Math.max(padding, cabeADireita ? rect.left : rect.right - largura);

    el.style.left = `${left}px`;
    el.style.maxHeight = `${maxHeight}px`;

    if (this.settings?.height && this.settings.height !== 'auto') {
      el.style.height = this.settings.height;
    }

    if (this.openUpward) {
      el.style.top = '';
      el.style.bottom = `${window.innerHeight - rect.top + gap}px`;
    } else {
      el.style.top = `${rect.bottom + gap}px`;
      el.style.bottom = '';
    }
  }

  private addListeners(): void {
    window.addEventListener('scroll', this.scrollHandler, {
      capture: true,
      passive: true
    });

    window.addEventListener('click', this.onCapture, { capture: true });
  }

  private removeListeners(): void {
    window.removeEventListener('scroll', this.scrollHandler, { capture: true });
    window.removeEventListener('click', this.onCapture, { capture: true });
  }

  toggleMenu(): void {
    this.isOpen ? this.closeMenu() : this.openMenu();
  }

  openMenu(): void {
    if (this.isOpen) {
      return;
    }
    this.isOpen = true;

    // *ngIf renderiza o elemento no próximo ciclo de CD. requestAnimationFrame
    // dispara após o CD, garantindo que menuContainer já aponta para o elemento
    // renderizado. Teleporta para o body para escapar de qualquer stacking context.
    requestAnimationFrame(() => {
      if (!this.isOpen || !this.menuContainer?.nativeElement) {
        return;
      }
      this.renderer.appendChild(
        this.document.body,
        this.menuContainer.nativeElement
      );
      this.applyPosition();
      this.addListeners();
    });

    this.opened.emit();
  }

  onMenuClick(_event: MouseEvent): void {
    if (this.settings?.closeOnSelect === false) {
      return;
    }
    this.closeMenu();
  }

  closeMenu(): void {
    if (!this.isOpen) {
      return;
    }

    const elemento = this.menuContainer?.nativeElement;
    if (elemento) {
      this.renderer.removeChild(elemento.parentNode, elemento);
    }

    this.isOpen = false;
    this.removeListeners();
    this.closed.emit();
  }
}
